import CloseRounded from '@mui/icons-material/CloseRounded'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { Box, Chip, IconButton, Popover, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { Fragment, memo, MouseEvent, useId, useState } from 'react'

import type { DetailConfig } from './FormSelect'

type Props<Option> = {
  values: readonly Option[]
  detailsConfig?: DetailConfig<Option>[]
  getOptionLabel: (option: Option) => string
  getOptionValue: (option: Option) => string
  inputLabel?: string
  disableTab?: boolean
  controlHeight?: number
}

/**
 * Ver los detalles de un select
 * @param param0
 * @param param0.values
 * @param param0.detailsConfig
 * @param param0.getOptionLabel
 * @param param0.getOptionValue
 * @param param0.inputLabel
 * @param param0.disableTab
 * @author isi-template
 * @constructor
 */
const FormSelectDetails = <Option,>({
  values,
  detailsConfig,
  getOptionLabel,
  getOptionValue,
  inputLabel,
  disableTab = false,
  controlHeight,
}: Props<Option>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const uniqueId = useId()
  const popoverId = `select-details-${uniqueId}`
  const titleId = `${popoverId}-title`
  const open = Boolean(anchorEl)
  const close = () => setAnchorEl(null)
  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    // Modal debe devolver el foco al botón, nunca al input del select.
    event.currentTarget.focus()
    setAnchorEl(event.currentTarget)
  }

  return (
    <>
      <Tooltip
        title={inputLabel ? `Ver detalles de ${inputLabel}` : 'Ver detalles de la selección'}
        disableInteractive
      >
        <IconButton
          type="button"
          disableFocusRipple
          tabIndex={disableTab ? -1 : 0}
          aria-label={inputLabel ? `Ver detalles de ${inputLabel}` : 'Ver detalles de la selección'}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? popoverId : undefined}
          onClick={handleOpen}
          sx={(theme) => ({
            width: 36,
            height: controlHeight,
            minHeight: 0,
            boxSizing: 'border-box',
            p: 0,
            alignSelf: 'stretch',
            flexShrink: 0,
            color: open ? 'primary.main' : 'inherit',
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: open ? 'primary.main' : 'divider',
            bgcolor: alpha(theme.palette.primary.main, open ? 0.12 : 0.04),
            '&:hover': { bgcolor: theme.palette.primary.hoverBgColor, borderColor: 'primary.main' },
            '&.Mui-focusVisible': {
              outline: `1px solid ${theme.palette.primary.main}`,
              outlineOffset: 0,
              bgcolor: theme.palette.primary.softBgColor,
            },
          })}
        >
          <InfoOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            id: popoverId,
            role: 'dialog',
            'aria-modal': true,
            'aria-labelledby': titleId,
            sx: {
              mt: 1,
              width: 340,
              maxWidth: 'calc(100vw - 32px)',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <InfoOutlined fontSize="small" color="primary" />
          <Typography id={titleId} variant="subtitle2" component="h2" sx={{ flex: 1 }}>
            {inputLabel ? `Detalles de ${inputLabel}` : 'Detalles de la selección'}
          </Typography>
          <IconButton autoFocus type="button" aria-label="Cerrar detalles" onClick={close} size="small">
            <CloseRounded fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ maxHeight: 'min(400px, 60vh)', overflowY: 'auto', overscrollBehavior: 'contain' }}>
          {values.map((option) => (
            <Box
              key={getOptionValue(option)}
              sx={{ p: 2, '& + &': { borderTop: '1px solid', borderColor: 'divider' } }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}
              >
                <Chip
                  label={getOptionValue(option)}
                  size="small"
                  variant="outlined"
                  sx={{ maxWidth: '45%', fontFamily: 'monospace', userSelect: 'text', flexShrink: 0 }}
                />
                <Typography
                  variant="subtitle2"
                  component="div"
                  sx={{ overflowWrap: 'anywhere', userSelect: 'text', textAlign: 'right', minWidth: 0 }}
                >
                  {getOptionLabel(option)}
                </Typography>
              </Box>
              {detailsConfig?.length ? (
                <Box
                  component="dl"
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.5fr)',
                    gap: 1,
                    mb: 0,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {detailsConfig.map((config, index) => (
                    <Fragment key={`${config.label}-${index}`}>
                      <Typography component="dt" variant="body2" color="text.secondary">
                        {config.label}
                      </Typography>
                      <Box
                        component="dd"
                        sx={{ m: 0, typography: 'body2', textAlign: 'right', userSelect: 'text' }}
                      >
                        {typeof config.accessor === 'function'
                          ? config.accessor(option)
                          : String(option[config.accessor] ?? '—')}
                      </Box>
                    </Fragment>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  Sin detalles adicionales.
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  )
}

export default memo(FormSelectDetails) as typeof FormSelectDetails
